import { HelpCircle } from "lucide-react";
import { useState } from "react";

import CheckBox from "@krea/shared/design-system/fields/Checkbox";
import {
  Field,
  FieldDescription,
  FieldError,
  FieldLabel,
} from "@krea/shared/design-system/input/field";
import { IntegerInput } from "@krea/shared/design-system/input/integer-input";
import {
  PercentOrMoneyInput,
  type PercentOrMoneyMode,
} from "@krea/shared/design-system/input/percent-or-money-input";
import { ResponsiveTooltip } from "@krea/shared/design-system/ResponsiveTooltip";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@krea/shared/design-system/Select";
import { Textarea } from "@krea/shared/design-system/Textarea";
import type { Currency } from "@krea/shared/types/common";

import type { IEarlyRepayment } from "./types";
import {
  EARLY_REPAYMENT_TYPE_LABELS,
  EarlyRepaymentType,
  EarlyRepaymentUnit,
} from "./types";

interface ITypeOption {
  value: EarlyRepaymentType;
  label: string;
  tooltip?: string;
}

const TYPE_OPTIONS: ITypeOption[] = [
  {
    value: EarlyRepaymentType.FREE,
    label: EARLY_REPAYMENT_TYPE_LABELS[EarlyRepaymentType.FREE],
  },
  {
    value: EarlyRepaymentType.FREE_AFTER_PERIOD,
    label: EARLY_REPAYMENT_TYPE_LABELS[EarlyRepaymentType.FREE_AFTER_PERIOD],
  },
  {
    value: EarlyRepaymentType.FEE_APPLIES,
    label: EARLY_REPAYMENT_TYPE_LABELS[EarlyRepaymentType.FEE_APPLIES],
  },
  {
    value: EarlyRepaymentType.FULL_INTEREST_AND_FEES,
    label:
      EARLY_REPAYMENT_TYPE_LABELS[EarlyRepaymentType.FULL_INTEREST_AND_FEES],
    tooltip:
      "No cost reduction, all remaining interest, fees and principal is paid",
  },
];

const HAS_MONTHS = new Set<EarlyRepaymentType>([
  EarlyRepaymentType.FREE_AFTER_PERIOD,
]);

const HAS_VALUE = new Set<EarlyRepaymentType>([
  EarlyRepaymentType.FREE_AFTER_PERIOD,
  EarlyRepaymentType.FEE_APPLIES,
]);

const modeFromUnit = (unit: EarlyRepaymentUnit | null): PercentOrMoneyMode =>
  unit === EarlyRepaymentUnit.AMOUNT ? "amount" : "percent";

const unitFromMode = (mode: PercentOrMoneyMode): EarlyRepaymentUnit =>
  mode === "amount" ? EarlyRepaymentUnit.AMOUNT : EarlyRepaymentUnit.PERCENT;

export interface IEarlyRepaymentFeeInputProps {
  value: IEarlyRepayment;
  onChange: (next: IEarlyRepayment) => void;
  currency: Currency;
  idPrefix: string;
  disabled?: boolean;
  errors?: Partial<Record<keyof IEarlyRepayment, string>>;
  /** Reveal every error regardless of interaction. Set once the form has been
   * submitted, so a blocked submit explains itself instead of failing with no
   * visible reason. */
  showAllErrors?: boolean;
}

type TermsField = keyof IEarlyRepayment;

export const EarlyRepaymentFeeInput = ({
  value: terms,
  onChange,
  currency,
  idPrefix,
  disabled = false,
  errors,
  showAllErrors = false,
}: IEarlyRepaymentFeeInputProps) => {
  // Which members the lender has actually engaged with. Without this, picking
  // a type immediately paints its sub-fields red: this component registers ONE
  // RHF field for the whole object, so changing the type validates the object
  // path and react-hook-form writes back errors for every member at once. A
  // normal field (monthly interest rate, say) is its own registration, so
  // changing a sibling never surfaces its error — this restores that parity.
  // Marked on change AND blur, which is what `mode: "all"` does for those
  // fields, rather than on focus: focusing and leaving a required field empty
  // is exactly when the error SHOULD appear.
  const [touched, setTouched] = useState<ReadonlySet<TermsField>>(
    () => new Set(),
  );
  const markTouched = (...keys: TermsField[]) =>
    setTouched((prev) => {
      if (keys.every((key) => prev.has(key))) return prev;
      const next = new Set(prev);
      for (const key of keys) next.add(key);
      return next;
    });
  const shownError = (key: TermsField) =>
    showAllErrors || touched.has(key) ? errors?.[key] : undefined;

  // Derived, not mirrored: with index-keyed bid lists (removeBid, replaceBid)
  // this component can be reused for a different bid, so a `useState`
  // seeded once from props would keep showing the PREVIOUS bid's reveal
  // state instead of the new one's stored comment (KREA-4218). A comment
  // must show whenever the current value actually has one; the local state
  // only covers the extra case of the user revealing the field while empty.
  const [manuallyRevealed, setManuallyRevealed] = useState(false);
  const hasComment = terms.comment != null || manuallyRevealed;

  const showMonths = HAS_MONTHS.has(terms.type ?? EarlyRepaymentType.FREE);
  const showValue = HAS_VALUE.has(terms.type ?? EarlyRepaymentType.FREE);

  // FREE may not carry a comment — core-service rejects it outright, on the
  // reasoning that there is nothing to explain when there is no charge, and a
  // note there reads as a hidden condition on an offer claiming to have none.
  const isFree = terms.type === EarlyRepaymentType.FREE;
  // The one case where hiding the field would strand the lender: migration
  // 0528 backfilled FREE rows WITH a comment, so opening one and saving is
  // refused. Hidden, that is an unexplainable failure; visible with its error,
  // it is a thing they can clear (or answer by picking the type that actually
  // describes their terms). It disappears once emptied.
  const strandedComment = isFree && Boolean(terms.comment?.trim());
  const showCommentBlock = !isFree || strandedComment;
  // A stranded comment is an EXISTING invalid state, not an error the lender
  // has not reached yet, so it bypasses the touched gate — otherwise the first
  // they learn of it is a save that refuses with the field showing nothing.
  const commentError = strandedComment
    ? errors?.comment
    : shownError("comment");
  // Nothing at all below the picker for a plain FREE bid, so the rail does not
  // render as an empty bordered strip.
  const showSubFields = showMonths || showValue || showCommentBlock;
  const mode = modeFromUnit(terms.unit);

  const selectedType = TYPE_OPTIONS.find(
    (option) => option.value === terms.type,
  );
  const selectedTypeLabel = selectedType?.label ?? "";
  const selectedTypeTooltip = selectedType?.tooltip ?? null;

  const handleTypeChange = (next: EarlyRepaymentType) => {
    markTouched("type");
    onChange({
      type: next,
      paidPeriodMonths: HAS_MONTHS.has(next) ? terms.paidPeriodMonths : null,
      unit: HAS_VALUE.has(next) ? terms.unit : null,
      value: HAS_VALUE.has(next) ? terms.value : null,
      comment: next === EarlyRepaymentType.FREE ? null : terms.comment,
    });
  };

  const handleMonthsChange = (months: number | null) => {
    markTouched("paidPeriodMonths");
    onChange({ ...terms, paidPeriodMonths: months });
  };

  // Preserve the value when only the unit changes. WithdrawalInput clears its sibling
  // because two mutually-exclusive columns are structurally forced to; here one value plus
  // one unit discriminator sits visibly together, so preserving is kinder than clearing.
  const handleModeChange = (nextMode: PercentOrMoneyMode) => {
    markTouched("unit", "value");
    onChange({ ...terms, unit: unitFromMode(nextMode) });
  };

  const handleValueChange = (nextValue: number | null) => {
    markTouched("unit", "value");
    onChange({
      ...terms,
      unit: terms.unit ?? unitFromMode(mode),
      value: nextValue,
    });
  };

  const handleCommentCheckboxChange = (checked: boolean) => {
    setManuallyRevealed(checked);
    if (!checked) {
      onChange({ ...terms, comment: null });
    }
  };

  const handleCommentChange = (comment: string) => {
    markTouched("comment");
    onChange({ ...terms, comment });
  };

  return (
    <div className="flex flex-col gap-4">
      <Field data-invalid={Boolean(shownError("type"))}>
        <FieldLabel id={`${idPrefix}-early-repayment-type-label`}>
          Cost for early repayment
          <span className="ml-0.5 text-red">*</span>
        </FieldLabel>
        <div className="flex items-center gap-2">
          <Select
            // Never `?? FREE`: an untyped legacy bid must read as "nothing
            // chosen", not as free of charge. New bids default to FREE explicitly.
            value={terms.type ?? undefined}
            onValueChange={(next) =>
              handleTypeChange(next as EarlyRepaymentType)
            }
            disabled={disabled}
          >
            <SelectTrigger
              data-test-id={`${idPrefix}-early-repayment-type`}
              aria-labelledby={`${idPrefix}-early-repayment-type-label`}
              aria-invalid={Boolean(shownError("type"))}
              aria-required={true}
              // SelectTrigger carries no invalid styling of its own, unlike
              // Input/InputGroup which read the group straight from
              // inputVariants. Same token, applied at the call site rather
              // than by editing a primitive every other Select also renders.
              className="w-full group-data-[invalid=true]/field:border-red"
            >
              <SelectValue placeholder="Select an option" />
            </SelectTrigger>
            <SelectContent>
              {TYPE_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {/* On the selected option, not each choice: a tooltip trigger inside
              a SelectItem competes with the item's own click target. */}
          {selectedTypeTooltip && (
            <ResponsiveTooltip
              trigger={
                <button
                  type="button"
                  aria-label={`About: ${selectedTypeLabel}`}
                  className="inline-flex shrink-0 cursor-help items-center justify-center rounded-full text-gray-600 hover:text-gray-500 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                >
                  <HelpCircle className="size-4" />
                </button>
              }
            >
              {selectedTypeTooltip}
            </ResponsiveTooltip>
          )}
        </div>
        <FieldError>{shownError("type")}</FieldError>
      </Field>

      {/* Rendered whenever the chosen type has anything beneath it, which is
          every type except a plain FREE. That one now has no sub-fields at
          all — no months, no fee, and no comment — so keeping the rail would
          leave an empty bordered strip under the picker. */}
      {showSubFields && (
        <div className="ml-1 flex flex-col gap-4 border-gray-200 border-l-2 pl-4">
          {showMonths && (
            <Field
              data-invalid={Boolean(shownError("paidPeriodMonths"))}
              className="gap-1"
            >
              {/* Shown iff required: HAS_MONTHS mirrors the schema's
                `.when("type", { is: FREE_AFTER_PERIOD })`. */}
              <FieldLabel htmlFor={`${idPrefix}-early-repayment-months`}>
                Fee applies for the first
                <span className="ml-0.5 text-red">*</span>
              </FieldLabel>
              <div className="flex items-center gap-2">
                <IntegerInput
                  id={`${idPrefix}-early-repayment-months`}
                  data-test-id={`${idPrefix}-early-repayment-months`}
                  value={terms.paidPeriodMonths}
                  onChange={handleMonthsChange}
                  onBlur={() => markTouched("paidPeriodMonths")}
                  disabled={disabled}
                  aria-invalid={Boolean(shownError("paidPeriodMonths"))}
                  aria-required={true}
                  className="max-w-24"
                />
                <span className="text-label">months</span>
              </div>
              <FieldError>{shownError("paidPeriodMonths")}</FieldError>
            </Field>
          )}

          {showValue && (
            <Field
              data-invalid={Boolean(shownError("value") || shownError("unit"))}
              className="gap-1"
            >
              {/* Shown iff required: HAS_VALUE mirrors the schema's NEEDS_FEE
                (FEE_APPLIES or FREE_AFTER_PERIOD). */}
              <FieldLabel htmlFor={`${idPrefix}-early-repayment-value`}>
                Fee
                <span className="ml-0.5 text-red">*</span>
              </FieldLabel>
              <PercentOrMoneyInput
                id={`${idPrefix}-early-repayment-value`}
                data-test-id={`${idPrefix}-early-repayment-value`}
                value={terms.value}
                onChange={handleValueChange}
                onBlur={() => markTouched("unit", "value")}
                mode={mode}
                onModeChange={handleModeChange}
                currency={currency}
                disabled={disabled}
                aria-invalid={Boolean(
                  shownError("value") || shownError("unit"),
                )}
                aria-required={true}
              />
              {/* Helper describes what the percentage is charged on; only accurate in percent mode */}
              {mode === "percent" && (
                <p className="text-helper">
                  A percentage is charged on the remaining principal.
                </p>
              )}
              <FieldError>{shownError("value")}</FieldError>
              <FieldError>{shownError("unit")}</FieldError>
            </Field>
          )}

          {showCommentBlock && (
            <div className="flex flex-col gap-2">
              {/* No way to ADD one on a free option — only to clear a stored one. */}
              {!isFree && (
                <CheckBox
                  id={`${idPrefix}-early-repayment-add-comment`}
                  label="Add a comment"
                  checked={hasComment}
                  onChange={handleCommentCheckboxChange}
                  disabled={disabled}
                />
              )}
              {hasComment && (
                <Field data-invalid={Boolean(commentError)} className="gap-1">
                  <FieldLabel htmlFor={`${idPrefix}-early-repayment-comment`}>
                    Comment
                  </FieldLabel>
                  <Textarea
                    id={`${idPrefix}-early-repayment-comment`}
                    data-test-id={`${idPrefix}-early-repayment-comment`}
                    value={terms.comment ?? ""}
                    onChange={(e) => handleCommentChange(e.target.value)}
                    onBlur={() => markTouched("comment")}
                    disabled={disabled}
                    aria-invalid={Boolean(commentError)}
                    className="group-data-[invalid=true]/field:border-red"
                  />
                  {/* Only on the stranded FREE case, and only because it is
                      true ONLY there: core-service withholds a FREE row's
                      comment from the customer schema (it returns null), while
                      every other type still returns its comment on every
                      surface. Saying this unconditionally would be a lie on
                      the types that ARE customer-visible. */}
                  {strandedComment && (
                    <FieldDescription>
                      This note is not shown to the customer.
                    </FieldDescription>
                  )}
                  <FieldError>{commentError}</FieldError>
                </Field>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
